import { useCallback, useEffect, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, Plus, Download, TrendingUp, TrendingDown, Scale, Trash2 } from "lucide-react";

const SOURCE_COLORS = {
  payment: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  expense: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  ap_bill: "text-orange-300 border-orange-500/30 bg-orange-500/10",
  adjustment: "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ label, value, sub, tone = "default", icon: Icon, testId }) {
  const toneClasses = {
    default: "border-white/10 bg-card",
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    bad: "border-red-500/20 bg-red-500/[0.03]",
  };
  return (
    <Card className={`p-4 ${toneClasses[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      </div>
      <div className="text-2xl font-semibold font-display">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function GLPage() {
  const { currentProperty } = useProperty();
  const [journal, setJournal] = useState({ items: [], totals: {}, pagination: {} });
  const [summary, setSummary] = useState(null);
  const [source, setSource] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const PAGE = 100;

  const load = useCallback(async (nextOffset = 0, append = false) => {
    if (!currentProperty?.id) return;
    setLoading(true);
    try {
      const params = { property_id: currentProperty.id, limit: PAGE, offset: nextOffset };
      if (source !== "all") params.source = source;
      if (start) params.start = start;
      if (end) params.end = end;
      const [j, s] = await Promise.all([
        api.get("/admin/gl/journal", { params }),
        api.get("/admin/gl/summary", { params: { property_id: currentProperty.id } }),
      ]);
      if (append) {
        setJournal((prev) => ({
          ...j.data,
          items: [...prev.items, ...j.data.items],
        }));
      } else {
        setJournal(j.data);
      }
      setSummary(s.data);
      setOffset(nextOffset);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [currentProperty?.id, source, start, end]);

  useEffect(() => { load(0, false); }, [load]);

  const loadMore = () => load(offset + PAGE, true);

  return (
    <div>
      <PageHeader
        eyebrow="Financials"
        title="General Ledger"
        subtitle="Unified journal — payments, expenses, vendor bills, and manual adjustments."
        actions={
          <div className="flex items-center gap-2">
            <AdjustmentDialog onCreated={load} propertyId={currentProperty?.id} />
            <Button
              variant="outline"
              className="border-white/10"
              data-testid="gl-export-statements-pdf"
              onClick={() => {
                const params = new URLSearchParams({ property_id: currentProperty?.id || "all" });
                window.open(`${API}/admin/finance/statements.pdf?${params.toString()}`, "_blank");
              }}
              title="Board-ready Balance Sheet + P&L + Cash Flow"
            >
              <Download className="h-4 w-4 mr-2" /> Statements PDF
            </Button>
            <Button
              variant="outline"
              className="border-white/10"
              data-testid="gl-export-csv"
              onClick={() => {
                const params = new URLSearchParams({ property_id: currentProperty?.id || "" });
                if (start) params.set("start", start);
                if (end) params.set("end", end);
                window.open(`${API}/admin/gl/journal.csv?${params.toString()}`, "_blank");
              }}
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        }
      />

      {/* --- KPI strip --- */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="gl-kpis">
          <Kpi
            label="YTD Income"
            value={money(summary.income?.ytd?.sum || 0)}
            sub={`${summary.income?.ytd?.count || 0} paid transactions · MTD ${money(summary.income?.mtd?.sum || 0)}`}
            tone="good" icon={TrendingUp} testId="gl-kpi-income"
          />
          <Kpi
            label="YTD Expense"
            value={money(summary.expense?.ytd?.sum || 0)}
            sub={`${summary.expense?.ytd?.count || 0} debits · MTD ${money(summary.expense?.mtd?.sum || 0)}`}
            tone="bad" icon={TrendingDown} testId="gl-kpi-expense"
          />
          <Kpi
            label="YTD Net"
            value={money(summary.net?.ytd || 0)}
            sub={`MTD ${money(summary.net?.mtd || 0)} · QTD ${money(summary.net?.qtd || 0)}`}
            tone={summary.net?.ytd >= 0 ? "good" : "bad"}
            icon={Scale} testId="gl-kpi-net"
          />
        </div>
      )}

      {/* --- Filter strip --- */}
      <Card className="border-white/10 bg-card p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="gl-filter-source" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="payment">Payments (income)</SelectItem>
                <SelectItem value="expense">Expenses</SelectItem>
                <SelectItem value="ap_bill">AP bills</SelectItem>
                <SelectItem value="adjustment">Manual adjustments</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="gl-filter-start" className="bg-white/[0.02] border-white/10" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="gl-filter-end" className="bg-white/[0.02] border-white/10" />
          </div>
          <div className="flex items-end">
            <Button onClick={() => load(0, false)} disabled={loading} data-testid="gl-refresh" className="bg-orange-500 hover:bg-orange-600 w-full">
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>
        </div>
      </Card>

      {/* --- Journal table --- */}
      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="overline flex items-center gap-2"><BookOpen className="h-3 w-3" /> Journal ({journal.totals?.count || 0} rows)</div>
          <div className="text-xs text-zinc-500">
            Credits <span className="text-emerald-300">{money(journal.totals?.credits || 0)}</span> · Debits <span className="text-amber-300">{money(journal.totals?.debits || 0)}</span> · Net <span className={journal.totals?.net >= 0 ? "text-emerald-300" : "text-red-300"}>{money(journal.totals?.net || 0)}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gl-journal-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Memo</th>
                <th className="text-left p-3 hidden md:table-cell">Party</th>
                <th className="text-right p-3">Credit</th>
                <th className="text-right p-3">Debit</th>
                <th className="text-right p-3 hidden md:table-cell">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {journal.items.map((r, idx) => (
                <tr key={`${r.source}-${r.id}-${idx}`} data-testid={`gl-row-${r.id}`} className="hover:bg-white/[0.02]">
                  <td className="p-3 whitespace-nowrap text-zinc-400">{r.date || "—"}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={SOURCE_COLORS[r.source] || "border-white/20"}>{r.source}</Badge>
                  </td>
                  <td className="p-3 max-w-[320px] truncate" title={r.memo}>{r.memo}</td>
                  <td className="p-3 hidden md:table-cell text-zinc-400 truncate max-w-[180px]" title={r.party}>{r.party || "—"}</td>
                  <td className="p-3 text-right text-emerald-300 font-mono">{r.credit ? money(r.credit) : "—"}</td>
                  <td className="p-3 text-right text-amber-300 font-mono">{r.debit ? money(r.debit) : "—"}</td>
                  <td className={"p-3 text-right hidden md:table-cell font-mono " + ((r.running_balance || 0) >= 0 ? "text-emerald-200" : "text-red-300")}>
                    {money(r.running_balance || 0)}
                  </td>
                </tr>
              ))}
              {journal.items.length === 0 && !loading && (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-500 text-sm">No ledger entries yet. Post an adjustment or record a payment to seed the journal.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {journal.pagination?.has_more && (
          <div className="border-t border-white/10 p-3 flex items-center justify-between text-xs text-zinc-500" data-testid="gl-pagination">
            <span>Showing {journal.items.length} of {journal.pagination.total || journal.totals.count}</span>
            <Button size="sm" variant="outline" className="border-white/10" onClick={loadMore} disabled={loading} data-testid="gl-load-more">
              {loading ? "Loading…" : `Load next ${PAGE}`}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function AdjustmentDialog({ onCreated, propertyId }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [sign, setSign] = useState("credit");
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = parseFloat(amount);
    if (!propertyId) return toast.error("Pick a property first");
    if (!n || Math.abs(n) < 0.01) return toast.error("Enter a non-zero amount");
    if (!memo.trim()) return toast.error("Add a memo");
    setSaving(true);
    try {
      await api.post("/admin/gl/adjustment", {
        property_id: propertyId,
        date, memo: memo.trim(),
        amount: sign === "credit" ? Math.abs(n) : -Math.abs(n),
      });
      toast.success("Adjustment posted");
      setOpen(false); setAmount(""); setMemo("");
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="gl-new-adjustment-btn" className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" /> Post adjustment
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="gl-adjustment-dialog">
        <DialogHeader>
          <DialogTitle>New journal adjustment</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Manual entries are for corrections + non-cash accruals. Signed: credit = income, debit = expense.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gl-adj-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={sign} onValueChange={setSign}>
                <SelectTrigger data-testid="gl-adj-sign" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (income)</SelectItem>
                  <SelectItem value="debit">Debit (expense)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="gl-adj-amount" />
            </div>
          </div>
          <div>
            <Label>Memo</Label>
            <Textarea rows={3} maxLength={200} placeholder="Late fee reversal for unit 4B, per board minutes 2026-02-01" value={memo} onChange={(e) => setMemo(e.target.value)} data-testid="gl-adj-memo" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="gl-adj-save">
            {saving ? "Posting…" : "Post to ledger"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
